import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"
import {
  getCustomerAreas,
  getCustomerArea,
  createCustomerArea,
  editCustomerArea,
  deleteCustomerArea,
  getAllCustomerAreas,
  getTrashedCustomerAreas,
  restoreCustomerArea,
} from "api/customerAreaApi"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const CUSTOMER_AREA_QUERY_KEY = "customer-areas"
export const CUSTOMER_AREA_GROUP_QUERY_KEY = "customer-areas-group"

// -------------------- FETCH PAGINATED --------------------
export const useCustomerAreas = (params = {}, enabled = true) => {
  return usePaginatedResource(
    CUSTOMER_AREA_QUERY_KEY,
    getCustomerAreas,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useCustomerArea = (id, enabled = true) => {
  return useResourceItem(
    CUSTOMER_AREA_QUERY_KEY,
    getCustomerArea,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateCustomerArea = () => {
  return useCreateResource(createCustomerArea, CUSTOMER_AREA_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditCustomerArea = () => {
  return useEditResource(editCustomerArea, CUSTOMER_AREA_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteCustomerArea = () => {
  return useDeleteResource(deleteCustomerArea, CUSTOMER_AREA_QUERY_KEY)
}

// -------------------- ADDITIONAL --------------------

// Get all customer areas without pagination
export const useAllCustomerAreas = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_AREA_QUERY_KEY,
    getAllCustomerAreas,
    { ...params, all: true },
    true
  )
}

// Get trashed customer areas
export const useTrashedCustomerAreas = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_AREA_QUERY_KEY,
    getTrashedCustomerAreas,
    params,
    true
  )
}

// Restore a trashed customer area
export const useRestoreCustomerArea = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCustomerArea,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([CUSTOMER_AREA_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([CUSTOMER_AREA_QUERY_KEY, id], restoredItem)
    },
  })
}
